import { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  CERTIFICATION_STATUSES,
  SettingsApi,
  type CertificationStatusName,
  type ProbabilityLevelInput,
} from "../api/settings";
import logger from "../analytics/logger";

// A local editable row. `probability` is kept as a string so the input can be
// cleared/typed freely; it is parsed on save.
type EditRow = { probability: string; status: CertificationStatusName };

function sortDesc(rows: EditRow[]): EditRow[] {
  return [...rows].sort(
    (a, b) => (Number(b.probability) || 0) - (Number(a.probability) || 0),
  );
}

export default function Settings() {
  const [rows, setRows] = useState<EditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const levels = await SettingsApi.listProbabilityLevels();
      setRows(
        levels.map((l) => ({ probability: String(l.probability), status: l.status })),
      );
    } catch (e: unknown) {
      logger.error("probability_levels_list_failed", e);
      setError("Failed to load thresholds");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void (async () => {
      await load();
    })();
  }, []);

  const updateRow = (index: number, patch: Partial<EditRow>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, { probability: "", status: "NEEDS_REVIEW" }]);

  const removeRow = (index: number) =>
    setRows((prev) => prev.filter((_, i) => i !== index));

  const save = async () => {
    setError(null);
    setNotice(null);

    const cleaned: ProbabilityLevelInput[] = [];
    const seen = new Set<number>();
    for (const r of rows) {
      const p = Number(r.probability);
      if (!Number.isInteger(p) || p < 0 || p > 100) {
        setError(`Each probability must be a whole number 0–100 (got "${r.probability}")`);
        return;
      }
      if (seen.has(p)) {
        setError(`Duplicate probability: ${p}`);
        return;
      }
      seen.add(p);
      cleaned.push({ probability: p, status: r.status });
    }
    if (cleaned.length === 0) {
      setError("At least one threshold is required");
      return;
    }
    if (!seen.has(0)) {
      setError("A probability = 0 row is required as the floor");
      return;
    }

    setSaving(true);
    try {
      const levels = await SettingsApi.replaceProbabilityLevels(cleaned);
      setRows(
        levels.map((l) => ({ probability: String(l.probability), status: l.status })),
      );
      setNotice("Thresholds saved");
    } catch (e: unknown) {
      logger.error("probability_levels_update_failed", e);
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Failed to save thresholds";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={6}>
      <Heading size="lg" mb={1}>
        Settings
      </Heading>
      <Heading size="sm" color="gray.600" mb={4} fontWeight="normal">
        Certification probability thresholds
      </Heading>

      <Text color="gray.600" fontSize="sm" mb={4} maxW="2xl">
        When a certifier assesses a work, its human-authorship probability (0–100%)
        is matched against these thresholds: the status of the highest threshold the
        score meets or exceeds is applied. The <b>0</b> row is the required floor.
      </Text>

      {error && (
        <Alert status="error" mb={4} maxW="2xl">
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {notice && (
        <Alert status="success" mb={4} maxW="2xl">
          <AlertIcon />
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <Box>
          <Spinner mr={3} />
          <Text as="span">Loading…</Text>
        </Box>
      ) : (
        <Box borderWidth="1px" borderRadius="md" p={4} maxW="2xl">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Probability ≥ (%)</Th>
                <Th>Status</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, index) => (
                <Tr key={index}>
                  <Td>
                    <Input
                      size="sm"
                      type="number"
                      min={0}
                      max={100}
                      value={row.probability}
                      onChange={(e) => updateRow(index, { probability: e.target.value })}
                      w="24"
                    />
                  </Td>
                  <Td>
                    <Select
                      size="sm"
                      value={row.status}
                      onChange={(e) =>
                        updateRow(index, {
                          status: e.target.value as CertificationStatusName,
                        })
                      }
                      w="52"
                    >
                      {CERTIFICATION_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Select>
                  </Td>
                  <Td textAlign="right">
                    <IconButton
                      aria-label="Remove threshold"
                      icon={<DeleteIcon />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => removeRow(index)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          <HStack mt={4} justify="space-between">
            <Button size="sm" variant="ghost" leftIcon={<AddIcon />} onClick={addRow}>
              Add threshold
            </Button>
            <HStack>
              <Button size="sm" variant="ghost" onClick={() => setRows(sortDesc(rows))}>
                Sort
              </Button>
              <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>
                Save
              </Button>
            </HStack>
          </HStack>
        </Box>
      )}
    </Box>
  );
}
